import { useEffect, useState } from 'react';
import * as R from 'remeda';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, useHistory } from 'react-router';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import {
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { notify } from '../../toaster';
import { DatasetDetailLayout } from '../dataset-detail/DatasetDetailLayout';
import { useDeleteDataset } from '../hooks/useDeleteDataset';
import { useOperations } from '../hooks/useOperations';
import { useTable } from '../hooks/useTable';
import { useUpdateTable } from '../hooks/useUpdateTable';
import { datasetsPath } from '../routes';
import { pageSpacingStyles } from '../styles';
import { Dataset, getColumnsFromMetadata } from '../types';
import { getTaskIdFromResponse } from '../utils';
import { ColumnDefinition } from './columns/ColumnDefinition';
import { formSchema, FormSchema } from './types';

const FORM_ID = 'dataset-settings';

export const DatasetSettings = ({ table }: { table: Dataset }) => {
  const intl = useIntl();
  const history = useHistory();
  const [deletingDataset, setDeletingDataset] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  const { mutate: updateTable, isLoading: isUpdating } = useUpdateTable();

  const { mutate: deleteDataset, isLoading: isDeletingDataset } =
    useDeleteDataset();

  const { data: operations } = useOperations({
    datasetName: table.name,
  });

  const deleteOperation = operations?.find(
    operation => operation.type === 'delete' && operation._id === deletingTaskId
  );

  const deletionInProgress = deletingTaskId !== null;

  useEffect(() => {
    if (deleteOperation?.status === 'FINISHED') {
      notify.default({
        title: intl.formatMessage({
          id: 'features.datasets.notification.deleteSucceeded',
        }),
      });

      history.push(datasetsPath());
    } else if (deleteOperation?.status === 'FAILED') {
      notify.error({
        title: intl.formatMessage({
          id: 'features.datasets.notification.deleteFailed',
        }),
      });
    }
  }, [deleteOperation, history, intl]);

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitSuccessful },
    reset,
  } = useForm<FormSchema>({
    defaultValues: {
      name: table.name,
      metadata: {
        ...table.metadata,
        columnNames: R.mapValues(
          table.schema?.properties ?? {},
          (_, key) => table.metadata.columnNames[key] ?? key
        ),
      },
    },
    resolver: zodResolver(formSchema(intl)),
    mode: 'onSubmit',
  });

  const schemaColumns = getColumnsFromMetadata(table.metadata, table.schema);

  const submitDataset = (form: FormSchema) => {
    if (!isValid) {
      return;
    }

    updateTable(
      {
        tableName: table.name,
        table: form,
      },
      {
        onSuccess: () => {
          notify.default({
            title: intl.formatMessage({
              id: 'features.datasets.notification.updateSucceeded',
            }),
          });
        },
        onError: () => {
          notify.error({
            title: intl.formatMessage({
              id: 'features.datasets.notification.updateFailed',
            }),
          });
        },
      }
    );
  };

  const lastOperationInProgress =
    operations?.[0]?.status === 'IN_PROGRESS' ||
    operations?.[0]?.status === 'WAITING';

  useEffect(() => {
    // Reset form state when form is submitted (so that you don't see leaving dialog)
    // TODO: isSubmitSuccessful does not actually check success now at all.
    reset(undefined, { keepValues: true });
  }, [isSubmitSuccessful, reset]);

  return (
    <DatasetDetailLayout
      table={table}
      tab="settings"
      buttons={[
        <Button
          key="delete-dataset-button"
          variant="outlined"
          color="secondary"
          disabled={deletionInProgress || lastOperationInProgress}
          startIcon={deletionInProgress ? <CircularProgress size={14} /> : null}
          onClick={() => {
            setDeletingDataset(true);
          }}
        >
          {intl.formatMessage({
            id: 'features.datasets.settings.header.button.delete',
          })}
        </Button>,
        <Button
          key="save-dataset-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          disabled={!isDirty || isUpdating || lastOperationInProgress}
          startIcon={isUpdating ? <CircularProgress size={14} /> : null}
        >
          {intl.formatMessage({
            id: 'features.datasets.settings.header.button.save',
          })}
        </Button>,
      ]}
    >
      <LeavingDialog when={isDirty} />

      <Stack
        sx={pageSpacingStyles}
        gap={2}
        component="form"
        id={FORM_ID}
        onSubmit={handleSubmit(submitDataset)}
      >
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.settings.form.idSection.title',
          })}
          description={
            <Stack spacing={1}>
              <Typography variant="inherit">
                {intl.formatMessage({
                  id: 'features.datasets.settings.form.idSection.description',
                })}
              </Typography>
              {/* TODO: Link */}
            </Stack>
          }
        >
          <Stack gap={2}>
            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.name' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.name.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
            />

            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.description' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.description.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              multiline
              maxRows={3}
            />

            {table.name && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {table.name}
                </Typography>
                <CopyToClipboardButton content={table.name} />
              </Stack>
            )}
          </Stack>
        </ContentGroup>
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.settings.form.columnsSection.title',
          })}
          description={intl.formatMessage({
            id: 'features.datasets.settings.form.columnsSection.description',
          })}
          isFullWidthLayout
        >
          <Stack spacing={0.5}>
            {schemaColumns.length > 0 ? (
              schemaColumns.map(column => (
                <ColumnDefinition
                  key={column.datasetAttributeName}
                  attributeName={column.datasetAttributeName}
                  type={column.type}
                  bsonType={column.bsonType}
                  control={control}
                />
              ))
            ) : (
              <Stack component={Paper} textAlign="center" p={2} elevation={2}>
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.datasets.settings.form.columnsSection.noColumns.title',
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        </ContentGroup>
      </Stack>

      <DeleteConfirmationDialog
        open={deletingDataset}
        title={intl.formatMessage(
          {
            id: 'features.datasets.settings.dialog.title',
          },
          { name: table.metadata.name }
        )}
        onConfirm={() => {
          deleteDataset(
            { datasetName: table.name },
            {
              onSuccess: r => {
                setDeletingDataset(false);

                // Subscribe to the task so that you can redirect and show message when it is done.
                setDeletingTaskId(getTaskIdFromResponse(r));
              },
            }
          );
        }}
        onCancel={() => setDeletingDataset(false)}
        description={intl.formatMessage({
          id: 'features.datasets.settings.dialog.text',
        })}
        isLoading={isDeletingDataset}
      />
    </DatasetDetailLayout>
  );
};

export const DatasetSettingsWrapper = ({
  match: {
    params: { id },
  },
}: RouteComponentProps<{ id: string }>) => {
  const { data: table } = useTable(id);
  return table ? <DatasetSettings table={table} /> : null;
};
